// Solar system catalogue. Radii in metres. Planet orbits: JPL "Approximate Positions of the
// Planets" Table 1 (valid 1800–2050), elements at J2000 + rates per Julian century.
// Rotation: IAU WGCCRE pole RA/Dec (deg, ICRF) and prime-meridian W0 + Wdot (deg, deg/day).

export type KeplerElements = {
  a: [number, number]; // AU, AU/cy
  e: [number, number];
  I: [number, number]; // deg
  L: [number, number]; // mean longitude
  varpi: [number, number]; // longitude of perihelion
  node: [number, number]; // longitude of ascending node
};

export type MoonOrbit = {
  /** circular orbit in the parent's equatorial plane */
  radius: number; // m
  periodDays: number;
  L0: number; // deg at J2000
};

export type AtmosphereDef = {
  height: number; // m
  rayleigh: [number, number, number]; // scattering coefficients at sea level (1/m)
  mie: number;
  rayleighScale: number; // m
  mieScale: number;
  sunIntensity: number;
};

export type BodyDef = {
  id: string;
  name: string;
  kind: 'star' | 'planet' | 'moon' | 'dwarf';
  radius: number;
  parent?: string;
  kepler?: KeplerElements;
  moonOrbit?: MoonOrbit;
  lunar?: boolean; // Earth's Moon: analytic lunar theory
  pole: { ra: number; dec: number };
  W0: number;
  Wdot: number;
  texture: string; // texture key (public/textures/{lo,hi}/<key>.jpg)
  tint?: [number, number, number];
  nightTexture?: string;
  cloudTexture?: string;
  normalTexture?: string;
  specTexture?: string;
  atmosphere?: AtmosphereDef;
  ring?: { inner: number; outer: number; texture: string };
  mapColor: string;
  /** sphere of influence radius (m) for frame dragging */
  soi: number;
  hasTerrain?: boolean;
};

const earthAtmosphere: AtmosphereDef = {
  height: 100_000,
  rayleigh: [5.802e-6, 13.558e-6, 33.1e-6],
  mie: 3.996e-6,
  rayleighScale: 8_000,
  mieScale: 1_200,
  sunIntensity: 6,
};

export const BODIES: BodyDef[] = [
  {
    id: 'sun', name: 'Sun', kind: 'star', radius: 696_340e3,
    pole: { ra: 286.13, dec: 63.87 }, W0: 84.176, Wdot: 14.1844,
    texture: 'sun', mapColor: '#ffd27a', soi: Infinity,
  },
  {
    id: 'mercury', name: 'Mercury', kind: 'planet', radius: 2_439.7e3,
    kepler: { a: [0.38709927, 0.00000037], e: [0.20563593, 0.00001906], I: [7.00497902, -0.00594749],
      L: [252.2503235, 149472.67411175], varpi: [77.45779628, 0.16047689], node: [48.33076593, -0.12534081] },
    pole: { ra: 281.0097, dec: 61.4143 }, W0: 329.5469, Wdot: 6.1385025,
    texture: 'mercury', mapColor: '#9e9a95', soi: 1.12e8,
  },
  {
    id: 'venus', name: 'Venus', kind: 'planet', radius: 6_051.8e3,
    kepler: { a: [0.72333566, 0.0000039], e: [0.00677672, -0.00004107], I: [3.39467605, -0.0007889],
      L: [181.9790995, 58517.81538729], varpi: [131.60246718, 0.00268329], node: [76.67984255, -0.27769418] },
    pole: { ra: 272.76, dec: 67.16 }, W0: 160.2, Wdot: -1.4813688,
    texture: 'venus', cloudTexture: 'venus_atmos', mapColor: '#e8c38a', soi: 6.16e8,
    atmosphere: { height: 250_000, rayleigh: [9e-6, 8e-6, 5e-6], mie: 6e-6, rayleighScale: 15_900, mieScale: 4_000, sunIntensity: 6 },
  },
  {
    id: 'earth', name: 'Earth', kind: 'planet', radius: 6_378_137,
    kepler: { a: [1.00000261, 0.00000562], e: [0.01671123, -0.00004392], I: [-0.00001531, -0.01294668],
      L: [100.46457166, 35999.37244981], varpi: [102.93768193, 0.32327364], node: [0, 0] },
    pole: { ra: 0, dec: 90 }, W0: 190.147, Wdot: 360.9856235,
    texture: 'earth_day', nightTexture: 'earth_night', cloudTexture: 'earth_clouds',
    normalTexture: 'earth_normal', specTexture: 'earth_spec',
    atmosphere: earthAtmosphere, mapColor: '#4f8fff', soi: 9.25e8, hasTerrain: true,
  },
  {
    id: 'moon', name: 'Moon', kind: 'moon', radius: 1_737.4e3, parent: 'earth', lunar: true,
    pole: { ra: 266.86, dec: 65.64 }, W0: 38.3213, Wdot: 13.17635815,
    texture: 'moon', mapColor: '#c8c8c8', soi: 6.6e7,
  },
  {
    id: 'mars', name: 'Mars', kind: 'planet', radius: 3_389.5e3,
    kepler: { a: [1.52371034, 0.00001847], e: [0.0933941, 0.00007882], I: [1.84969142, -0.00813131],
      L: [-4.55343205, 19140.30268499], varpi: [-23.94362959, 0.44441088], node: [49.55953891, -0.29257343] },
    pole: { ra: 317.68143, dec: 52.8865 }, W0: 176.63, Wdot: 350.89198226,
    texture: 'mars', mapColor: '#d0643a', soi: 5.77e8,
    atmosphere: { height: 60_000, rayleigh: [19.9e-6, 13.6e-6, 5.8e-6], mie: 2e-6, rayleighScale: 11_000, mieScale: 3_000, sunIntensity: 5 },
  },
  {
    id: 'ceres', name: 'Ceres', kind: 'dwarf', radius: 469.7e3,
    kepler: { a: [2.7675, 0], e: [0.0758, 0], I: [10.593, 0],
      L: [153.9 + 6.07, 7824.7], varpi: [153.9, 0], node: [80.305, 0] },
    pole: { ra: 291.418, dec: 66.764 }, W0: 170.65, Wdot: 952.1532,
    texture: 'ceres', mapColor: '#8f8a84', soi: 7.7e7,
  },
  {
    id: 'jupiter', name: 'Jupiter', kind: 'planet', radius: 69_911e3,
    kepler: { a: [5.202887, -0.00011607], e: [0.04838624, -0.00013253], I: [1.30439695, -0.00183714],
      L: [34.39644051, 3034.74612775], varpi: [14.72847983, 0.21252668], node: [100.47390909, 0.20469106] },
    pole: { ra: 268.056595, dec: 64.495303 }, W0: 284.95, Wdot: 870.536,
    texture: 'jupiter', mapColor: '#d8b48c', soi: 4.82e10,
  },
  {
    id: 'io', name: 'Io', kind: 'moon', radius: 1_821.6e3, parent: 'jupiter',
    moonOrbit: { radius: 421_700e3, periodDays: 1.769137786, L0: 106.07719 },
    pole: { ra: 268.05, dec: 64.5 }, W0: 200.39, Wdot: 203.4889538,
    texture: 'moon', tint: [1.25, 1.05, 0.45], mapColor: '#e7d36a', soi: 7.8e6,
  },
  {
    id: 'europa', name: 'Europa', kind: 'moon', radius: 1_560.8e3, parent: 'jupiter',
    moonOrbit: { radius: 671_034e3, periodDays: 3.551181, L0: 175.73161 },
    pole: { ra: 268.08, dec: 64.51 }, W0: 36.022, Wdot: 101.3747235,
    texture: 'moon', tint: [1.15, 1.05, 0.9], mapColor: '#d9cbb3', soi: 9.7e6,
  },
  {
    id: 'ganymede', name: 'Ganymede', kind: 'moon', radius: 2_634.1e3, parent: 'jupiter',
    moonOrbit: { radius: 1_070_412e3, periodDays: 7.15455296, L0: 120.55883 },
    pole: { ra: 268.2, dec: 64.57 }, W0: 44.064, Wdot: 50.3176081,
    texture: 'moon', tint: [0.95, 0.9, 0.85], mapColor: '#a89e92', soi: 3.2e7,
  },
  {
    id: 'callisto', name: 'Callisto', kind: 'moon', radius: 2_410.3e3, parent: 'jupiter',
    moonOrbit: { radius: 1_882_709e3, periodDays: 16.6890184, L0: 84.44459 },
    pole: { ra: 268.72, dec: 64.83 }, W0: 259.51, Wdot: 21.5710715,
    texture: 'moon', tint: [0.7, 0.64, 0.58], mapColor: '#7d7166', soi: 3.7e7,
  },
  {
    id: 'saturn', name: 'Saturn', kind: 'planet', radius: 58_232e3,
    kepler: { a: [9.53667594, -0.0012506], e: [0.05386179, -0.00050991], I: [2.48599187, 0.00193609],
      L: [49.95424423, 1222.49362201], varpi: [92.59887831, -0.41897216], node: [113.66242448, -0.28867794] },
    pole: { ra: 40.589, dec: 83.537 }, W0: 38.9, Wdot: 810.7939024,
    texture: 'saturn', mapColor: '#e3cf9a', soi: 5.46e10,
    ring: { inner: 74_500e3, outer: 140_220e3, texture: 'saturn_ring' },
  },
  {
    id: 'titan', name: 'Titan', kind: 'moon', radius: 2_574.7e3, parent: 'saturn',
    moonOrbit: { radius: 1_221_870e3, periodDays: 15.945, L0: 15.0 },
    pole: { ra: 39.4827, dec: 83.4279 }, W0: 186.5855, Wdot: 22.5769768,
    texture: 'venus_atmos', tint: [1.1, 0.8, 0.45], mapColor: '#d9a55a', soi: 4.3e7,
    atmosphere: { height: 400_000, rayleigh: [4e-6, 5e-6, 7e-6], mie: 9e-6, rayleighScale: 40_000, mieScale: 20_000, sunIntensity: 6 },
  },
  {
    id: 'uranus', name: 'Uranus', kind: 'planet', radius: 25_362e3,
    kepler: { a: [19.18916464, -0.00196176], e: [0.04725744, -0.00004397], I: [0.77263783, -0.00242939],
      L: [313.23810451, 428.48202785], varpi: [170.9542763, 0.40805281], node: [74.01692503, 0.04240589] },
    pole: { ra: 257.311, dec: -15.175 }, W0: 203.81, Wdot: -501.1600928,
    texture: 'uranus', mapColor: '#9fe3ea', soi: 5.18e10,
  },
  {
    id: 'neptune', name: 'Neptune', kind: 'planet', radius: 24_622e3,
    kepler: { a: [30.06992276, 0.00026291], e: [0.00859048, 0.00005105], I: [1.77004347, 0.00035372],
      L: [-55.12002969, 218.45945325], varpi: [44.96476227, -0.32241464], node: [131.78422574, -0.00508664] },
    pole: { ra: 299.36, dec: 43.46 }, W0: 253.18, Wdot: 536.3128492,
    texture: 'neptune', mapColor: '#4b70dd', soi: 8.66e10,
  },
  {
    id: 'pluto', name: 'Pluto', kind: 'dwarf', radius: 1_188.3e3,
    kepler: { a: [39.48211675, -0.00031596], e: [0.2488273, 0.0000517], I: [17.14001206, 0.00004818],
      L: [238.92903833, 145.20780515], varpi: [224.06891629, -0.04062942], node: [110.30393684, -0.01183482] },
    pole: { ra: 132.993, dec: -6.163 }, W0: 302.695, Wdot: 56.3625225,
    texture: 'pluto', mapColor: '#c9b8a3', soi: 3.1e9,
  },
];

export const BODY_BY_ID = new Map(BODIES.map((b) => [b.id, b]));
